import React, { FC, useState, useEffect } from 'react';
import _ from 'lodash';
import {
  Box,
  Divider,
  TextButton,
  Text,
  Loader,
  Heading,
} from '@wix/design-system';
import { useRouter } from 'next/router';
import {
  exploreLoadPage,
  exploreResultsPageGenericClick,
} from '@wix/bi-logger-marketplace/v2';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import useMobileMediaQuery from '@components/Hooks/useMobileMediaQuery';
import { useExploreContext } from '@components/Contexts/ExploreContext';
import { useHeaderContext } from '@components/Contexts/HeaderContext';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { updateQueryParams } from '@utils/route';
import { useBi } from '@components/Hooks/useBi';
import { explorePath } from '@components/Hooks/useRouter';
import Header from '@components/Header';
import { getWixLoginUrl } from '@utils/urlUtils';
import PartnerDetails from './Filters/PartnerDetails';
import Services from './Filters/Services';
import Budget from './Filters/Budget';
import Providers from './Providers';
import SelectedFilterTags from './SelectedFilterTags';
import PaginationFooter from '@components/PaginationFooter';
import MobileFiltersAndActions from './MobileFiltersAndActions';
import HeaderBanner from '@components/HeaderBanner';
import { exploreDataHooks } from './Explore.dataHooks';
import s from './Explore.module.scss';

function SpecialStateMessage({
  dataHook,
  title,
  subtitle,
}: {
  dataHook: string;
  title: string;
  subtitle: string;
}) {
  return (
    <Box
      dataHook={dataHook}
      align="center"
      paddingTop="66px"
      direction="vertical"
      paddingBottom="110px"
    >
      <Box
        padding="42px"
        direction="vertical"
        align="center"
        textAlign="center"
      >
        <Box marginBottom="SP2">
          <Heading size="large">{title}</Heading>
        </Box>
        <Box className={s.specialStateSubtitle}>
          <Heading size="medium" as="span">
            {subtitle}
          </Heading>
        </Box>
      </Box>
    </Box>
  );
}

type Props = {
  host: string;
};

const Explore: FC<Props> = ({ host }) => {
  const {
    state: {
      providers,
      appStatus,
      numberOfPages,
      numberOfResults,
      selectedServices,
      selectedLanguages,
      selectedPricing,
      selectedLocation,
      entry,
      origin,
      biArenaType,
      currentPage,
      isMobileUserAgent,
      language,
      category,
      service,
    },
  } = useExploreContext();
  const router = useRouter();
  const { t } = useTranslation();
  const logger = useBi();
  const { userDetails } = useHeaderContext();
  const { sessionId } = useBiSessionContext();
  const isMobileView = useMobileMediaQuery(isMobileUserAgent);
  const briefURL = `${host}/marketplace/brief-auto-match?entry=explore&origin=results-page-bottom`;

  const [matchURL, setMatchURL] = useState('');
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    if (_.isNil(userDetails)) {
      setMatchURL(
        `${getWixLoginUrl(language)}&redirectTo=${encodeURIComponent(
          briefURL,
        )}`,
      );
    } else {
      setMatchURL(briefURL);
    }
  }, [briefURL, userDetails]);

  useEffect(() => {
    if (appStatus === 'loading') {
      window && window.scrollTo(0, 0);
    }
  }, [appStatus]);

  useEffect(() => {
    if (appStatus !== 'loading' && isFirstLoad) {
      const providersList = _.map(providers, (provider) => {
        return provider.id;
      });

      logger.report(
        exploreLoadPage({
          sessionId,
          origin,
          entry,
          typeid: biArenaType,
          exploreVersion: 'V2',
          partnerList: JSON.stringify({ providersList }),
          numOfResults: numberOfResults,
          filterValue: JSON.stringify({
            selectedServices,
            selectedLanguages,
            selectedPricing,
            selectedLocation,
          }),
        }),
      );
      setIsFirstLoad(false);
    }
  }, [
    appStatus,
    biArenaType,
    entry,
    isFirstLoad,
    numberOfResults,
    origin,
    selectedLanguages,
    selectedLocation,
    selectedPricing,
    selectedServices,
    sessionId,
  ]);

  function renderError() {
    return (
      <SpecialStateMessage
        dataHook={exploreDataHooks.errorState()}
        title={t('explore-error-title')}
        subtitle={t('explore-error-subtitle')}
      />
    );
  }

  function renderEmptyState() {
    return (
      <SpecialStateMessage
        dataHook={exploreDataHooks.emptyState()}
        title={t('explore-no-results-title')}
        subtitle={t('explore-no-results-subtitle')}
      />
    );
  }

  function renderAutoMatch() {
    return (
      <>
        {numberOfPages <= 1 && !isMobileView ? (
          <Box
            padding={
              providers.length > 0 ? '67px 0 36px' : '0px 112px 36px 112px'
            }
          >
            <Divider skin="light" />
          </Box>
        ) : (
          <></>
        )}
        <Box
          verticalAlign="middle"
          align="center"
          padding={isMobileView ? '24px' : 0}
        >
          <Text className={s.autoMatch}>
            {t('explore-need-help')}&nbsp;
            <TextButton
              skin="dark"
              dataHook={exploreDataHooks.briefAutoMatch()}
              as="a"
              href={matchURL}
              target="_blank"
              underline="always"
              onClick={() => {
                logger.report(
                  exploreResultsPageGenericClick({
                    clickType: 'help finding professional',
                    sessionId,
                    origin,
                    entry,
                    exploreVersion: 'V2',
                    data: JSON.stringify({ currentPage }),
                  }),
                );
              }}
            >
              {t('explore-find-right-match')}
            </TextButton>
          </Text>
        </Box>
      </>
    );
  }

  function headerOnClick() {
    logger.report(
      exploreResultsPageGenericClick({
        clickType: 'marketplace top button',
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data: '',
      }),
    );
  }

  function getPageUrl(page: number) {
    return `${explorePath}${category ? `/${category.pathName}` : ''}${
      service ? `/${service.pathName}` : ''
    }${updateQueryParams(page > 1 ? { page } : {}, router)}`;
  }

  function onPageClicked(page: number) {
    logger.report(
      exploreResultsPageGenericClick({
        clickType: 'specific page choice',
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data: JSON.stringify({
          pageChosen: page,
          currentPage,
        }),
      }),
    );
  }

  function renderExploreBanner() {
    return (
      <HeaderBanner
        dataHook={exploreDataHooks.title()}
        isMobileView={isMobileView}
        subtitleKey="explore-page-heading-subtitle"
        titleKey="explore-page-heading-multiple-services"
        className={isMobileView ? '' : s.bannerWrapperBoxShadowOrange}
      />
    );
  }

  if (isMobileView) {
    return (
      <Box
        backgroundColor="#eceff3"
        display="block"
        dataHook={exploreDataHooks.mobileContainer()}
        minHeight="100vh"
        className={s.mobileExplorePage}
      >
        <Header
          onClick={headerOnClick}
          pageName="explore"
          sessionId={sessionId}
        />
        <div className={s.topTitleWrapper}>{renderExploreBanner()}</div>
        <MobileFiltersAndActions />
        <div className={s.page}>
          {appStatus === 'error' ? (
            renderError()
          ) : (
            <>
              <div>
                {appStatus === 'loading' ? (
                  <Box marginTop="10%" align="center" verticalAlign="middle">
                    <Loader size="large" />
                  </Box>
                ) : (
                  <>
                    {_.size(providers) > 0 ? (
                      <>
                        <Providers infiniteScroll={true} />
                        {numberOfPages > 1 && (
                          <div
                            className={s.seoOnlyPagination}
                            aria-hidden="true"
                          >
                            <PaginationFooter
                              currentPage={currentPage}
                              numberOfPages={numberOfPages}
                              getPageUrl={getPageUrl}
                              onPageClicked={onPageClicked}
                              showTopDivider={false}
                            />
                          </div>
                        )}
                        <Box paddingTop="47px">
                          <Divider skin="light" />
                        </Box>
                      </>
                    ) : (
                      renderEmptyState()
                    )}
                  </>
                )}
              </div>
              {appStatus !== 'loading' && renderAutoMatch()}
            </>
          )}
        </div>
      </Box>
    );
  }

  function renderStickyFilterHeader() {
    return (
      <div className={s.stickyNav}>
        <Box backgroundColor="#fff" direction="vertical">
          <div>
            <div className={s.innerNav} data-hook="filters">
              <Box marginTop="18px" marginBottom="18px">
                <div
                  style={{
                    marginLeft: '0px',
                    marginRight: '55px',
                  }}
                >
                  <Services />
                </div>
                <div
                  style={{
                    marginRight: '55px',
                  }}
                >
                  <PartnerDetails />
                </div>
                <div>
                  <Budget />
                </div>
              </Box>
            </div>
            <Divider />
            <div className={s.innerNav}>
              <Box direction="vertical">
                <SelectedFilterTags />
                <div style={{ height: '0px' }}>&nbsp;</div>
              </Box>
            </div>
            <Divider />
          </div>
        </Box>
      </div>
    );
  }

  return (
    <Box
      dataHook={exploreDataHooks.container()}
      minHeight="100vh"
      direction="vertical"
      backgroundColor="#eceff3"
    >
      <Header
        onClick={headerOnClick}
        sessionId={sessionId}
        pageName="explore"
      />

      <div className={s.topTitleWrapper}>{renderExploreBanner()}</div>
      {renderStickyFilterHeader()}
      <div className={s.page}>
        {appStatus === 'error' ? (
          renderError()
        ) : (
          <div>
            {appStatus === 'loading' ? (
              <Box marginTop="10%" align="center" verticalAlign="middle">
                <Loader size="large" />
              </Box>
            ) : (
              <>
                {_.size(providers) > 0 ? (
                  <>
                    <Providers infiniteScroll={false} />
                    {numberOfPages > 1 ? (
                      <PaginationFooter
                        currentPage={currentPage}
                        numberOfPages={numberOfPages}
                        getPageUrl={getPageUrl}
                        onPageClicked={onPageClicked}
                        showTopDivider
                      />
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  renderEmptyState()
                )}
              </>
            )}
          </div>
        )}
      </div>
      <Box direction="vertical" className={s.autoMatchContainer}>
        {appStatus !== 'loading' && renderAutoMatch()}
      </Box>
    </Box>
  );
};

export default Explore;
